<template>
  <aside
    class="admin-sidebar"
    :class="props.collapsed ? 'admin-sidebar--collapsed' : ''"
  >
    <div class="sidebar-header">
      <img :src="compactLogo" alt="BORAsuite" class="logo-mark" width="38" height="38" />
      <div v-if="!props.collapsed" class="logo-text">
        <img :src="darkLogo" alt="BORAsuite Admin" class="sidebar-full-logo" />
        <span class="tagline">Platform Control Center</span>
      </div>
    </div>

    <nav class="sidebar-nav">
      <ul>
        <li
          v-for="menu in visibleMenus"
          :key="menu.label"
          class="menu-group"
          :class="isOpen(menu.label) ? 'is-open' : ''"
        >
          <button
            type="button"
            class="menu-parent"
            :class="isGroupActive(menu) ? 'active' : ''"
            :data-label="menu.label"
            :title="props.collapsed ? menu.label : undefined"
            @click="toggleMenu(menu)"
          >
            <span class="icon">
              <i :class="menu.icon" aria-hidden="true"></i>
            </span>
            <span v-if="!props.collapsed" class="menu-label">{{ menu.label }}</span>

            <span v-if="(menu.children.length || (menu.appGroups || []).length) && !props.collapsed" class="chevron">
              <i
                class="fa-solid"
                :class="isOpen(menu.label) ? 'fa-chevron-up' : 'fa-chevron-down'"
                aria-hidden="true"
              ></i>
            </span>
          </button>

          <ul
            v-if="(menu.children.length || (menu.appGroups || []).length) && !props.collapsed"
            class="submenu"
            v-show="isOpen(menu.label)"
          >
            <template v-if="menu.appGroups?.length">
              <li v-for="group in menu.appGroups" :key="group.label" class="app-group">
                <button
                  type="button"
                  class="app-group-toggle"
                  :class="isAppGroupActive(group) ? 'active' : ''"
                  @click="toggleAppGroup(menu.label, group.label)"
                >
                  <span class="menu-label">{{ group.label }}</span>
                  <i
                    class="fa-solid"
                    :class="isAppGroupOpen(menu.label, group.label) ? 'fa-chevron-up' : 'fa-chevron-down'"
                    aria-hidden="true"
                  ></i>
                </button>

                <ul v-show="isAppGroupOpen(menu.label, group.label)" class="app-group-links">
                  <li v-for="child in group.children" :key="`${group.label}-${child.label}`" class="submenu-item">
                    <a
                      class="submenu-link app-nested-link"
                      :class="isRouteNameActive(child.routeName) ? 'active' : ''"
                      href="#"
                      @click.prevent="go(child)"
                    >
                      <span class="bullet">
                        <i class="fa-solid fa-angle-right" aria-hidden="true"></i>
                      </span>
                      <span class="menu-label">{{ child.label }}</span>
                    </a>
                  </li>
                </ul>
              </li>
            </template>

            <template v-else>
              <li v-for="child in menu.children" :key="child.label" class="submenu-item">
                <a
                  class="submenu-link"
                  :class="isRouteNameActive(child.routeName) ? 'active' : ''"
                  :data-label="child.label"
                  href="#"
                  @click.prevent="go(child)"
                >
                  <span class="bullet">
                    <i class="fa-solid fa-angle-right" aria-hidden="true"></i>
                  </span>
                  <span class="menu-label">{{ child.label }}</span>
                </a>
              </li>
            </template>
          </ul>
        </li>
      </ul>
    </nav>

    <!-- ── Collapse toggle button at bottom of sidebar ── -->
    <button
      type="button"
      class="sidebar-collapse-btn"
      :title="props.collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="$emit('toggle')"
    >
      <i
        class="fa-solid"
        :class="props.collapsed ? 'fa-chevron-right' : 'fa-chevron-left'"
        aria-hidden="true"
      ></i>
      <span>Collapse</span>
    </button>

  </aside>
</template>

<script setup lang="ts">
import compactLogo from '../../assets/images/icon-logo.png'
import darkLogo from '../../assets/images/logo-BORAsuite-2.png'
  import { computed, ref, watchEffect } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useAuthStore } from '../../stores/auth'
  import { usePermissionGuardStore } from '../../stores/permissionGuard'
  import { useUiStore } from '../../stores/ui'
  import { ROLE_GROUPS } from '../../constants/roleGroups'

  const props = defineProps<{ collapsed: boolean }>()
  const emit = defineEmits<{ (e: 'toggle'): void }>()

  const router = useRouter()
  const route = useRoute()
  const auth = useAuthStore()
  const permissions = usePermissionGuardStore()
  const ui = useUiStore()

  type MenuChild = {
    label: string
    routeName: string
    requires?: string
    requiresPermission?: string
    requiresAnyPermissions?: string[]
  }

  type MenuAppGroup = {
    label: string
    requires?: string
    requiresPermission?: string
    requiresAnyPermissions?: string[]
    children: MenuChild[]
  }

  type MenuItem = {
    label: string
    icon: string
    routeName?: string
    requires?: string
    requiresPermission?: string
    requiresAnyPermissions?: string[]
    // Grants access when the `requires` role check fails (permission-scoped admin roles).
    orAnyPermissions?: string[]
    children: MenuChild[]
    appGroups?: MenuAppGroup[]
  }

  const menus: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'fa-solid fa-gauge-high',
      routeName: 'admin.dashboard',
      requires: 'ADMIN_PANEL',
      children: [],
    },
    {
      label: 'Clients',
      icon: 'fa-solid fa-building',
      routeName: 'admin.clients.index',
      requires: 'ADMINS',
      orAnyPermissions: ['admin.clients.view'],
      children: [],
    },
    {
      label: 'Billing',
      icon: 'fa-solid fa-file-invoice-dollar',
      requiresAnyPermissions: [
        'admin.billing.view',
        'admin.billing.subscriptions.view',
        'admin.billing.payments.view',
        'core.billing_extra_pricing.view',
      ],
      children: [
        { label: 'Overview', routeName: 'admin.billing.index', requiresPermission: 'admin.billing.view' },
        {
          label: 'Subscriptions',
          routeName: 'admin.billing.subscriptions',
          requiresPermission: 'admin.billing.subscriptions.view',
        },
        { label: 'Invoices', routeName: 'admin.billing.invoices', requiresPermission: 'admin.billing.view' },
        { label: 'Payments', routeName: 'admin.billing.payments', requiresPermission: 'admin.billing.payments.view' },
        {
          label: 'Extra Pricing',
          routeName: 'admin.billing.extra-pricing',
          requiresPermission: 'core.billing_extra_pricing.view',
        },
      ],
    },
    {
      label: 'Support',
      icon: 'fa-solid fa-headset',
      routeName: 'admin.support.index',
      requires: 'ADMIN_PANEL',
      children: [
        { label: 'Tickets', routeName: 'admin.support.tickets', requires: 'ADMIN_PANEL' },
        { label: 'Knowledge Base', routeName: 'admin.support.knowledge', requires: 'ADMINS' },
        { label: 'Live Chat', routeName: 'admin.support.chat', requires: 'ADMIN_PANEL' },
        { label: 'Feedback', routeName: 'admin.support.feedback', requires: 'ADMIN_PANEL' },
        { label: 'Announcements', routeName: 'admin.support.announcements', requires: 'ADMINS' },
        { label: 'System Status', routeName: 'admin.support.status', requires: 'ADMIN_PANEL' },
      ],
    },
    {
      label: 'CMS',
      icon: 'fa-solid fa-newspaper',
      requiresAnyPermissions: ['admin.cms.view', 'admin.cms.manage'],
      children: [
        { label: 'Posts', routeName: 'admin.cms.posts', requiresAnyPermissions: ['admin.cms.view', 'admin.cms.manage'] },
        { label: 'Forum', routeName: 'admin.cms.forum', requiresAnyPermissions: ['admin.cms.view', 'admin.cms.manage'] },
        { label: 'Tags', routeName: 'admin.cms.tags', requiresAnyPermissions: ['admin.cms.view', 'admin.cms.manage'] },
        { label: 'Settings', routeName: 'admin.cms.settings', requiresAnyPermissions: ['admin.cms.view', 'admin.cms.manage'] },
      ],
    },
    {
      label: 'Help Center',
      icon: 'fa-solid fa-book-open',
      requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
      children: [
        { label: 'Overview', routeName: 'admin.helpcenter', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
        { label: 'Articles', routeName: 'admin.helpcenter.articles', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
        { label: 'Categories', routeName: 'admin.helpcenter.categories', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
        { label: 'Media Library', routeName: 'admin.helpcenter.media', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
        { label: 'Analytics', routeName: 'admin.helpcenter.analytics', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
        { label: 'Portal Preview', routeName: 'admin.helpcenter.portal', requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'] },
      ],
    },
    {
      label: 'Apps',
      icon: 'fa-solid fa-table-cells-large',
      requires: 'ADMIN_PANEL',
      children: [],
      appGroups: [
        {
          label: 'BORAinvoice',
          requires: 'ADMIN_PANEL',
          children: [
            { label: 'Tenants', routeName: 'admin.apps.invoice.tenants', requires: 'ADMIN_PANEL' },
            { label: 'Documents', routeName: 'admin.apps.invoice.documents', requires: 'ADMIN_PANEL' },
            { label: 'Payments', routeName: 'admin.apps.invoice.payments', requires: 'ADMIN_PANEL' },
            { label: 'Expenses', routeName: 'admin.apps.invoice.expenses', requires: 'ADMIN_PANEL' },
          ],
        },
        {
          label: 'BORApos',
          requires: 'ADMIN_PANEL',
          children: [
            { label: 'Tenants', routeName: 'admin.apps.pos.tenants', requires: 'ADMIN_PANEL' },
            { label: 'Products', routeName: 'admin.apps.pos.products', requires: 'ADMIN_PANEL' },
            { label: 'Customers', routeName: 'admin.apps.pos.customers', requires: 'ADMIN_PANEL' },
          ],
        },
        {
          label: 'BORApharma',
          requiresAnyPermissions: ['admin.borapharma.view', 'pharma_config.view', 'admin.clients.view'],
          children: [
            { label: 'Overview', routeName: 'admin.apps.pharma.overview', requiresAnyPermissions: ['admin.borapharma.view', 'pharma_config.view'] },
            {
              label: 'Tenants / Users',
              routeName: 'admin.apps.pharma.tenants-users',
              requiresAnyPermissions: ['admin.borapharma.view', 'pharma_config.view', 'admin.clients.view'],
            },
            { label: 'Config', routeName: 'admin.apps.pharma.config', requiresAnyPermissions: ['admin.borapharma.view', 'pharma_config.view'] },
            {
              label: 'Import Medicine List',
              routeName: 'admin.apps.pharma.medicine-import',
              requiresAnyPermissions: ['admin.borapharma.manage', 'pharma_config.create'],
            },
            {
              label: 'Insurance Companies',
              routeName: 'admin.apps.pharma.insurance-companies',
              requiresAnyPermissions: ['admin.patient_care.view', 'admin.patient_care.manage', 'admin.borapharma.view'],
            },
          ],
        },
        {
          label: 'BORAlearn',
          requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'],
          children: [
            { label: 'Students', routeName: 'admin.boralearn.students', requires: 'ADMINS' },
            { label: 'Teachers', routeName: 'admin.boralearn.teachers', requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'] },
            { label: 'Schools', routeName: 'admin.boralearn.schools', requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'] },
            { label: 'Parents', routeName: 'admin.boralearn.parents', requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'] },
            { label: 'Parent/Learner Requests', routeName: 'admin.boralearn.parent-requests', requires: 'ADMIN_PANEL' },
            {
              label: 'Question Bank',
              routeName: 'admin.boralearn.question-bank',
              requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'],
            },
            { label: 'Badges & Ranks', routeName: 'admin.boralearn.badges', requiresAnyPermissions: ['admin.learn.view', 'admin.learn.manage'] },
            { label: 'Lessons', routeName: 'admin.boralearn.lessons', requires: 'ADMIN_PANEL' },
            { label: 'Settings', routeName: 'admin.boralearn.settings', requires: 'ADMIN_PANEL' },
            { label: 'Branding & Social Links', routeName: 'admin.boralearn.branding', requires: 'ADMIN_PANEL' },
          ],
        },
        {
          label: 'BORAmembers',
          requires: 'ADMIN_PANEL',
          children: [
            { label: 'Members', routeName: 'admin.apps.member.members', requires: 'ADMIN_PANEL' },
            { label: 'Groups', routeName: 'admin.apps.member.groups', requires: 'ADMIN_PANEL' },
            { label: 'Events', routeName: 'admin.apps.member.events', requires: 'ADMIN_PANEL' },
          ],
        },
      ],
    },
    {
      label: 'Shared Directory',
      icon: 'fa-solid fa-folder-tree',
      requiresAnyPermissions: ['shared_directory.view'],
      children: [
        { label: 'Master Products', routeName: 'admin.shared-directory.products', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Product Images', routeName: 'admin.shared-directory.product-images', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Product Variants', routeName: 'admin.shared-directory.product-variants', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Product Types', routeName: 'admin.shared-directory.product-types', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Product Categories', routeName: 'admin.shared-directory.product-categories', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Brands / Manufacturers', routeName: 'admin.shared-directory.brands', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Units of Measure', routeName: 'admin.shared-directory.units', requiresAnyPermissions: ['shared_directory.view'] },
        { label: 'Countries / Local Data', routeName: 'admin.shared-directory.country-data', requiresAnyPermissions: ['shared_directory.view'] },
      ],
    },
    {
      label: 'Reports',
      icon: 'fa-solid fa-chart-column',
      requiresAnyPermissions: ['admin.clients.view'],
      children: [
        {
          label: 'Client Status',
          routeName: 'admin.reports.client-status',
          requiresAnyPermissions: ['admin.clients.view'],
        },
      ],
    },
    {
      label: 'Changelogs',
      icon: 'fa-solid fa-clipboard-list',
      requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
      children: [
        {
          label: 'All Updates',
          routeName: 'admin.system.changelogs',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        {
          label: 'Pending',
          routeName: 'admin.system.changelogs.pending',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        {
          label: 'Completed',
          routeName: 'admin.system.changelogs.completed',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        {
          label: 'Client Visible',
          routeName: 'admin.system.changelogs.client-visible',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        {
          label: 'Roadmap',
          routeName: 'admin.system.changelogs.roadmap',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
      ],
    },
    {
      label: 'Settings',
      icon: 'fa-solid fa-gear',
      requiresAnyPermissions: [
        'admin.settings.view',
        'admin.settings.manage',
        'admin.settings.users.view',
        'admin.settings.users.manage',
        'admin.settings.roles.view',
        'admin.settings.roles.manage',
        'admin.settings.billing.view',
        'admin.settings.billing.manage',
      ],
      children: [
        {
          label: 'Company Settings',
          routeName: 'admin.system.company-settings',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        {
          label: 'Admin Users',
          routeName: 'admin.system.admins',
          requiresAnyPermissions: ['admin.settings.users.view', 'admin.settings.users.manage'],
        },
        {
          label: 'Roles & Permissions',
          routeName: 'admin.system.roles',
          requiresAnyPermissions: ['admin.settings.roles.view', 'admin.settings.roles.manage'],
        },
        {
          label: 'Billing Settings',
          routeName: 'admin.system.billing-settings',
          requiresAnyPermissions: ['admin.settings.billing.view', 'admin.settings.billing.manage'],
        },
        {
          label: 'Tenant Access Policies',
          routeName: 'admin.system.tenant-access-policies',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        { label: 'APIs & Payment Gateways', routeName: 'admin.system.apis', requires: 'SUPER_ADMIN' },
        {
          label: 'Logs',
          routeName: 'admin.system.logs',
          requiresAnyPermissions: ['admin.settings.view', 'admin.settings.manage'],
        },
        { label: 'Backups', routeName: 'admin.system.backups', requires: 'SUPER_ADMIN' },
      ],
    },
    {
      label: 'BORAstore',
      icon: 'fa-solid fa-store',
      routeName: 'admin.store.index',
      requires: 'ADMIN_PANEL',
      children: [],
    },
  ]

  function userHasAnyRole(roleCodes: readonly string[]) {
    if (auth.user?.is_super_admin) return true
    const roles = auth.roles || []
    const exactMatch = roleCodes.some((r) => roles.some((role) => role.role_code === r))
    if (exactMatch) return true

    const isSuperOnly = roleCodes.length > 0 && roleCodes.every((code) => code.endsWith('_super_admin'))
    if (isSuperOnly) return false

    const expectsAdminRoleFamily = roleCodes.some((code) => code.startsWith('admin_'))
    if (!expectsAdminRoleFamily) return false

    return roles.some((role) => {
      const appCode = String(role.app_code || '').toLowerCase()
      const roleCode = String(role.role_code || '').toLowerCase()
      return (
        appCode === 'admin' ||
        roleCode.startsWith('admin_') ||
        roleCode === 'administrator' ||
        roleCode === 'admin'
      )
    })
  }

  function canAccess(entry?: {
    requires?: string
    requiresPermission?: string
    requiresAnyPermissions?: string[]
  }) {
    if (!entry) return true

    const hasPermission = (code: string) =>
      permissions.permissionCodes.length ? permissions.hasPermission(code) : auth.hasPermission(code)
    const hasAnyPermission = (codes: string[]) =>
      permissions.permissionCodes.length ? permissions.hasAnyPermission(codes) : auth.hasAnyPermission(codes)

    if (entry.requiresPermission && !hasPermission(entry.requiresPermission)) {
      return false
    }

    if (entry.requiresAnyPermissions?.length) {
      if (!hasAnyPermission(entry.requiresAnyPermissions)) {
        return false
      }
    }

    if (!entry.requires) return true
    const roleCodes = ROLE_GROUPS[entry.requires as keyof typeof ROLE_GROUPS] || []
    if (userHasAnyRole(roleCodes)) return true
    const orAnyPermissions = (entry as MenuItem).orAnyPermissions
    return !!orAnyPermissions?.length && hasAnyPermission(orAnyPermissions)
  }

  const visibleMenus = computed<MenuItem[]>(() => {
    return menus
      .filter((menu) => canAccess(menu))
      .map((menu) => {
        const children = (menu.children || []).filter((child) => canAccess(child))
        const appGroups = (menu.appGroups || [])
          .filter((group) => canAccess(group))
          .map((group) => ({
            ...group,
            children: (group.children || []).filter((child) => canAccess(child)),
          }))
          .filter((group) => group.children.length > 0)

        return {
          ...menu,
          children,
          appGroups,
        }
      })
      .filter((menu) => menu.routeName || menu.children.length > 0 || (menu.appGroups || []).length > 0)
  })

  const openMenus = ref<string[]>([])
  const openAppGroups = ref<Record<string, string[]>>({})

  const isOpen = (label: string) => openMenus.value.includes(label)

  function isRouteNameActive(routeName?: string) {
    if (!routeName) return false
    if (route.name === routeName) return true

    const currentRouteName = String(route.name || '')
    if (currentRouteName.startsWith(`${routeName}.`)) return true
    if (routeName === 'admin.billing.invoices' && currentRouteName === 'admin.billing.invoice-detail') return true
    if (routeName === 'admin.billing.payments' && currentRouteName === 'admin.billing.payment-detail') return true

    return false
  }

  function isAppGroupActive(group: MenuAppGroup) {
    return group.children.some((child) => isRouteNameActive(child.routeName))
  }

  function isGroupActive(menu: MenuItem & { appGroups?: MenuAppGroup[] }) {
    if (isRouteNameActive(menu.routeName)) return true
    if (menu.children.some((child) => isRouteNameActive(child.routeName))) return true
    return (menu.appGroups || []).some((group) => isAppGroupActive(group))
  }

  function isAppGroupOpen(menuLabel: string, groupLabel: string) {
    return (openAppGroups.value[menuLabel] || []).includes(groupLabel)
  }

  function toggleAppGroup(menuLabel: string, groupLabel: string) {
    const current = openAppGroups.value[menuLabel] || []
    if (current.includes(groupLabel)) {
      openAppGroups.value[menuLabel] = current.filter((item) => item !== groupLabel)
      return
    }
    openAppGroups.value[menuLabel] = [...current, groupLabel]
  }

  watchEffect(() => {
    const currentGroup = visibleMenus.value.find((menu) => isGroupActive(menu))
    if (currentGroup && (currentGroup.children.length || (currentGroup.appGroups || []).length)) {
      if (!openMenus.value.includes(currentGroup.label)) {
        openMenus.value = [...openMenus.value, currentGroup.label]
      }
    }

    for (const menu of visibleMenus.value) {
      const activeGroupLabels = (menu.appGroups || [])
        .filter((group: MenuAppGroup) => isAppGroupActive(group))
        .map((group: MenuAppGroup) => group.label)
      if (activeGroupLabels.length) {
        const existing = openAppGroups.value[menu.label] || []
        const merged = Array.from(new Set([...existing, ...activeGroupLabels]))
        openAppGroups.value[menu.label] = merged
      }
    }
  })

  function toggleMenu(menu: MenuItem & { appGroups?: MenuAppGroup[] }) {
    const hasNestedLinks = menu.children.length > 0 || (menu.appGroups || []).length > 0
    if (!hasNestedLinks) {
      if (!canAccess(menu)) {
        ui.showToast('You have no privileges to access this page.', 'warning')
        return
      }
      if (menu.routeName) router.push({ name: menu.routeName })
      return
    }

    if (isOpen(menu.label)) {
      openMenus.value = openMenus.value.filter((l) => l !== menu.label)
    } else {
      openMenus.value = [...openMenus.value, menu.label]
    }
  }

  function go(child: MenuChild) {
    if (!canAccess(child)) {
      ui.showToast('You have no privileges to access this page.', 'warning')
      return
    }
    router.push({ name: child.routeName })
  }
</script>
